import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import PageAlert from "../../components/PageAlert/PageAlert";
import { fetchDashboard } from "../../api/dashboardApi";
import { getStoredUser } from "../../api/client";
import { getApiErrorMessage, handleApiError } from "../../utils/apiError";
import "./dashboard.scss";

const number = new Intl.NumberFormat("en-MY");

const DashboardPage = () => {
  const navigate = useNavigate();
  const user = getStoredUser();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDashboard()
      .then(setDashboard)
      .catch((requestError) => {
        if (handleApiError(requestError, navigate)) return;
        setError(getApiErrorMessage(requestError));
      });
  }, [navigate]);

  const stats = useMemo(() => {
    const data = dashboard?.stats;
    return [
      { label: "Total products", value: data ? number.format(data.total_products) : "—", meta: data ? `Across ${number.format(data.total_categories)} categories` : "Loading catalogue", icon: "ph:package" },
      { label: "Visible online", value: data ? number.format(data.visible_products) : "—", meta: data ? `${data.visible_percentage}% of catalogue` : "Loading visibility", icon: "ph:eye" },
      { label: "Featured products", value: data ? number.format(data.featured_products) : "—", meta: "Shown first on storefront", icon: "ph:star" },
      { label: "Admin users", value: data ? number.format(data.total_admins) : "—", meta: data ? `${number.format(data.active_admins_this_month)} active this month` : "Loading administrators", icon: "ph:users" },
    ];
  }, [dashboard]);

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const date = now.toLocaleDateString("en-MY", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const firstName = user?.name?.split(/\s+/)[0] || "Administrator";
  const updates = dashboard?.recent_updates || [];

  return (
    <div className="dashboard-page">
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}
      <section className="dashboard-page__welcome">
        <div><p>{date}</p><h2>{greeting}, {firstName}.</h2><span>Here is what is happening with your product catalogue.</span></div>
        <Link to="/admin/products" className="dashboard-page__primary">
          Manage products <Icon icon="ph:arrow-right" height={14} />
        </Link>
      </section>
      <section className="dashboard-page__stats">
        {stats.map((item) => (
          <article className="dashboard-page__stat" key={item.label}>
            <div className="dashboard-page__stat-top">
              <span><Icon icon={item.icon} height={16} /></span>
            </div>
            <p>{item.label}</p>
            <h3>{item.value}</h3>
            <small>{item.meta}</small>
          </article>
        ))}
      </section>
      <section className="dashboard-page__grid">
        <article className="dashboard-page__panel dashboard-page__panel--wide">
          <div className="dashboard-page__panel-head"><div><p>CATALOGUE ACTIVITY</p><h3>Recent updates</h3></div><Link to="/admin/products">View all</Link></div>
          <div className="dashboard-page__updates">
            {updates.map((update) => (
              <div className="dashboard-page__update" key={update.id}>
                <span className="dashboard-page__update-icon">
                  <Icon icon="ph:package" height={14} />
                </span>
                <div><strong>{update.name}</strong><small>{update.action}</small></div>
                <span>{update.actor}</span>
                <time dateTime={update.updated_at || undefined}>{update.updated_ago || "—"}</time>
              </div>
            ))}
            {dashboard && !updates.length && <div className="dashboard-page__empty">No catalogue updates recorded yet.</div>}
            {!dashboard && !error && <div className="dashboard-page__empty">Loading recent updates…</div>}
          </div>
        </article>
        <article className="dashboard-page__panel">
          <div className="dashboard-page__panel-head"><div><p>QUICK ACTIONS</p><h3>Store controls</h3></div></div>
          <Link className="dashboard-page__action" to="/admin/products">
            <span><Icon icon="ph:plus" height={16} /></span>
            <div><strong>Add a product</strong><small>Create a new storefront listing</small></div>
            <b><Icon icon="ph:caret-right" height={14} /></b>
          </Link>
          <Link className="dashboard-page__action" to="/admin/products">
            <span><Icon icon="ph:list" height={16} /></span>
            <div><strong>Arrange products</strong><small>Control featured display order</small></div>
            <b><Icon icon="ph:caret-right" height={14} /></b>
          </Link>
          <Link className="dashboard-page__action" to="/admin/users">
            <span><Icon icon="ph:users" height={16} /></span>
            <div><strong>Manage users</strong><small>Administrator accounts</small></div>
            <b><Icon icon="ph:caret-right" height={14} /></b>
          </Link>
        </article>
      </section>
    </div>
  );
};

export default DashboardPage;
